import { useEffect } from 'react';

const DOCK_OFFSET_VAR = '--adjustment-modal-dock-offset';

const readDockOffset = (): number => {
    const pal = document.querySelector('pal-chatbot') as HTMLElement | null;
    if (!pal || !pal.hasAttribute('data-dock-open')) return 0;
    const handle = document.getElementById('pal-dock-resize-handle');
    return Math.round(pal.offsetWidth + (handle?.offsetWidth ?? 0));
};

const writeDockOffset = (px: number) => {
    document.documentElement.style.setProperty(DOCK_OFFSET_VAR, `${px}px`);
};

/** Applies PAL dock width as a CSS variable while `enabled`. No React state — avoids modal re-renders. */
export const usePalDockOffset = (enabled: boolean): void => {
    useEffect(() => {
        if (!enabled) {
            writeDockOffset(0);
            return;
        }

        let last = -1;
        let raf = 0;
        const apply = () => {
            const next = readDockOffset();
            if (next === last) return;
            last = next;
            writeDockOffset(next);
        };
        const schedule = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                apply();
            });
        };

        apply();
        const pal = document.querySelector('pal-chatbot');
        const handle = document.getElementById('pal-dock-resize-handle');
        const resizeObserver = new ResizeObserver(schedule);
        if (pal) resizeObserver.observe(pal);
        if (handle) resizeObserver.observe(handle);
        const mutations = new MutationObserver(schedule);
        if (pal) mutations.observe(pal, { attributes: true, attributeFilter: ['data-dock-open'] });

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            resizeObserver.disconnect();
            mutations.disconnect();
            writeDockOffset(0);
        };
    }, [enabled]);
};
